import PropTypes from 'prop-types'
import React from 'react'
import onClickOutside from 'react-onclickoutside'
import { injectIntl, FormattedMessage } from 'react-intl'
import { DropTarget } from 'react-dnd'
import { findDOMNode } from 'react-dom'
import scrollIntoView from 'scroll-into-view-if-needed'
import userDragType from 'components/User/dragType'
import _ from 'lodash'
import getCharCount from '../../utils/getCharCount'
import getCharCountFromEditorState from '../../utils/getCharCountFromEditorState'
import {
  convertKanBoFormatToContentState,
  convertEditorStateToKanBoFormat,
  mentionMutability
} from '../../utils/convert'
import Immutable, { fromJS } from 'immutable'
import deepEqual from 'deep-equal'
import isPromise from 'is-promise'
import { getData as apiGetData } from 'api/Api'
import Editor from 'draft-js-plugins-editor'
import {
  EditorState,
  ContentState,
  Modifier,
  RichUtils,
  KeyBindingUtil,
  getDefaultKeyBinding,
  DefaultDraftBlockRenderMap
} from 'draft-js'
import createMentionPlugin from 'draft-js-mention-plugin'
import createLinkPlugin from '../../plugins/link'
import { addHttpToUrl } from 'utils/url'
import pathRegex from 'utils/pathRegex'
import {
  ButtonGroup,
  SplitButton,
} from 'react-bootstrap'
import Button from 'components/Button'
import Loader from 'components/Loader'
import KanBoIcon from 'components/KanBoIcon'
import 'draft-js/dist/Draft.css'
import sweetalert from 'sweetalert2'
import mentionTheme from './MentionTheme.module.scss'
import classes from './Editor.module.scss'
import User from 'components/ControlledUser'

const MentionEntry = ({
  mention,
  theme,
  searchValue,
  className,
  ...parentProps
}) => {
  return (
    <div
      className={`${className} ${mentionTheme.mentionEntry}`}
      {...parentProps}
    >
      <User
        avatar
        id={mention.get('id')}
        size={20}
        className={mentionTheme.mentionEntryAvatar}
      />
      {mention.get('name')}
    </div>
  )
}

export class StyleButton extends React.Component {
  constructor () {
    super()
    this.onToggle = (e) => {
      e.preventDefault()
      this.props.onToggle(this.props.style)
    }
  }

  render () {
    const {
      icon,
      active
    } = this.props

    return (
      <Button
        bsStyle={active ? 'primary' : 'default'}
        className={classes.styleButton}
        onMouseDown={this.onToggle}
        icon={icon}
      />
    )
  }
}

const INLINE_STYLES = [
  {icon: <KanBoIcon name='kanbo-16-bold' />, style: 'BOLD'},
  {icon: <KanBoIcon name='kanbo-16-italic' />, style: 'ITALIC'},
  {icon: <KanBoIcon name='kanbo-16-underline' />, style: 'UNDERLINE'}
]

const InlineStyleControls = (props) => {
  const currentStyle = props.editorState.getCurrentInlineStyle()
  return (
    <div className={classes.controlsGroup}>
      <ButtonGroup>
        {INLINE_STYLES.map((type, index) =>
          <StyleButton
            key={index}
            active={currentStyle.has(type.style)}
            icon={type.icon}
            onToggle={props.onToggle}
            style={type.style}
          />
        )}
      </ButtonGroup>
    </div>
  )
}

const BlockStyleControls = (props) => {
  const { blockTypes, editorState } = props
  const selection = editorState.getSelection()
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey())
    .getType()

  return (
    <div className={classes.controlsGroup}>
      <ButtonGroup>
        {blockTypes.map((type, index) =>
          <StyleButton
            key={index}
            active={type.style === blockType}
            icon={type.icon}
            onToggle={props.onToggle}
            style={type.style}
          />
        )}
      </ButtonGroup>
    </div>
  )
}

export class CheckboxBlock extends React.Component {
  render () {
    const { checked } = this.props

    let compClassName = 'public-DraftStyleDefault-ul'
    if (checked) {
      compClassName += ' ' + classes.checkedList
    } else {
      compClassName += ' ' + classes.uncheckedList
    }

    return (
      <ul className={compClassName}>
        {this.props.children}
      </ul>
    )
  }
}

const blockRenderMap = Immutable.Map({
  'unchecked-list-item': {
    element: 'li',
    wrapper: <CheckboxBlock />
  },
  'checked-list-item': {
    element: 'li',
    wrapper: <CheckboxBlock checked />
  }
})

const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap)

const positionSuggestions = ({ state, props }) => {
  let transform

  if (state.isActive && props.suggestions.size > 0) {
    transform = 'scaleY(1)'
  } else if (state.isActive) {
    transform = 'scaleY(0)'
  }

  return {
    transform
  }
}

export class RichTextEditor extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,
    connectUserDropTarget: PropTypes.func.isRequired,

    unsavedValue: PropTypes.object,

    addUnsavedChange: PropTypes.func,
    removeUnsavedChange: PropTypes.func,

    content: PropTypes.object,
    error: PropTypes.string,
    submitting: PropTypes.bool,
    mentionBoardId: PropTypes.number,
    readOnly: PropTypes.bool,
    autoFocus: PropTypes.bool,
    checklistBlockTypes: PropTypes.bool,
    className: PropTypes.string,
    maxLength: PropTypes.number,
    placeholder: PropTypes.string,
    saveChanges: PropTypes.string,
    expandOnFocus: PropTypes.bool,
    submitButton: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.string
    ]),
    closeAfterSubmit: PropTypes.bool,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
  }

  static defaultProps = {
    readOnly: false,
    expandOnFocus: false,
    submitButton: <FormattedMessage id='SAVE' />,
    closeAfterSubmit: true
  }

  constructor (props) {
    super(props)

    const mentionPlugin = createMentionPlugin({
      theme: {
        mentionSuggestions: mentionTheme.mentionSuggestions,
        mentionSuggestionsEntry: 'list-group-item',
        mentionSuggestionsEntryFocused: 'list-group-item active'
      },
      entityMutability: mentionMutability,
      positionSuggestions,
      mentionComponent: (props) => (<a>{props.children}</a>)
    })
    const { MentionSuggestions } = mentionPlugin

    const linkPlugin = createLinkPlugin()

    this.plugins = [
      mentionPlugin,
      linkPlugin
    ]
    this.components = { MentionSuggestions }

    this.blockTypes = [
      {icon: <KanBoIcon name='kanbo-16-unsorted-list' />, style: 'unordered-list-item'},
      {icon: <KanBoIcon name='kanbo-16-sorted-list' />, style: 'ordered-list-item'}
    ]
    if (props.checklistBlockTypes) {
      this.blockTypes = [
        ...this.blockTypes,
        {icon: <KanBoIcon name='kanbo-16-check-box-unchecked' />, style: 'unchecked-list-item'},
        {icon: <KanBoIcon name='kanbo-16-solid-check-box-checked' />, style: 'checked-list-item'}
      ]
    }
    this.blockTypes = [
      ...this.blockTypes,
      {icon: <KanBoIcon name='kanbo-16-quote-right' />, style: 'blockquote'}
    ]

    const editorState =
      props.unsavedValue
        ? EditorState.createWithContent(convertKanBoFormatToContentState(props.unsavedValue))
        : props.content
          ? EditorState.createWithContent(convertKanBoFormatToContentState(props.content))
          : EditorState.createEmpty()

    this.state = {
      editorState,
      content: convertEditorStateToKanBoFormat(editorState),
      charCount: getCharCountFromEditorState(editorState),
      suggestions: Immutable.fromJS([]),
      isLoadingMentions: false,
      isFocused: !!this.props.autoFocus,
      showTextFormatting: false,
    }

    this.showLinkModal = false

    this.clearFetchUsers = this.clearFetchUsers.bind(this)
    this.fetchUsers = this.fetchUsers.bind(this)
    this.blur = this.blur.bind(this)
    this.focusEditor = this.focusEditor.bind(this)
    this.clear = this.clear.bind(this)
    this.submit = this.submit.bind(this)
    this.handleEditorRef = this.handleEditorRef.bind(this)
    this.handleUnsavedChange = _.debounce(this.handleUnsavedChange.bind(this), 200)
    this.handleCharCount = _.throttle(this.handleCharCount.bind(this), 200)
    this.handleChange = this.handleChange.bind(this)
    this.handleClick = this.handleClick.bind(this)
    this.handleEditorMouseDown = this.handleEditorMouseDown.bind(this)
    this.handleEditorFocus = this.handleEditorFocus.bind(this)
    this.handleEditorBlur = this.handleEditorBlur.bind(this)
    this.handleHeaderStylesToggle = this.handleHeaderStylesToggle.bind(this)
    this.keyBindingFn = this.keyBindingFn.bind(this)
    this.handleReturn = this.handleReturn.bind(this)
    this.handleKeyCommand = this.handleKeyCommand.bind(this)
    this.handleDrop = this.handleDrop.bind(this)
    this.onTab = this.onTab.bind(this)
    this.toggleBlockType = this.toggleBlockType.bind(this)
    this.toggleInlineStyle = this.toggleInlineStyle.bind(this)
    this.handleSearchChange = this.handleSearchChange.bind(this)
    this.handleTextFormattingClick = this.handleTextFormattingClick.bind(this)
    this.handleInsertLinkClick = this.handleInsertLinkClick.bind(this)
    this.handleSubmitClick = this.handleSubmitClick.bind(this)
    this.handleCancelClick = this.handleCancelClick.bind(this)
  }

  componentDidMount = () => {
    if (this.props.autoFocus) {
      this.focusRequestedFrame = requestAnimationFrame(() => {
        this.focusEditor()
      })
    }

    if (this.state.isFocused) {
      this.scrollIntoView()
    }
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (
      (this.state.isFocused && !prevState.isFocused) ||
      (this.state.isFocused && this.editorState !== prevState.editorState)
    ) {
      this.scrollIntoView()
    }
  }

  componentWillReceiveProps = (nextProps) => {
    if (this.props.readOnly &&
        nextProps.content &&
        nextProps.content !== this.props.content) {
      const contentState =
        nextProps.content
          ? convertKanBoFormatToContentState(nextProps.content)
          : ContentState.createFromText('')

      this.handleChange(EditorState.push(this.state.editorState, contentState))
    }
  }

  componentWillUnmount = () => {
    cancelAnimationFrame(this.focusRequestedFrame)
    cancelAnimationFrame(this.scrollRequestedFrame)
    
    this.clearFetchUsers()
    this.handleCharCount.cancel()
  }

  scrollIntoView = () => {
    cancelAnimationFrame(this.scrollRequestedFrame)
    this.scrollRequestedFrame = requestAnimationFrame(() => {
      scrollIntoView(findDOMNode(this), {
        scrollMode: 'if-needed',
        block: 'nearest'
      })
    })
  }

  handleClickOutside () {
    this.editor && this.editor.blur()
  }

  clearFetchUsers () {
    if (this._fetchUsersTimeout) {
      clearTimeout(this._fetchUsersTimeout)
      delete this._fetchUsersTimeout
    }

    if (this._fetchUsersPromise && this._fetchUsersPromise.cancel) {
      this._fetchUsersPromise.cancel()
      delete this._fetchUsersPromise
    }
  }

  fetchUsers (keyword) {
    const { mentionBoardId } = this.props

    this.clearFetchUsers()

    // TODO: MentionSuggestions' componentWillReceiveProps hides mentions when passed empty suggestions
    this.setState({
      suggestions: Immutable.fromJS([{
        id: 0,
        name: ''
      }]),
      isLoadingMentions: true
    })

    this._fetchUsersTimeout = setTimeout(() => {
      this._fetchUsersPromise = apiGetData('MentionableBoardUsers', { id: mentionBoardId, query: keyword, limit: 5 })
      this._fetchUsersPromise.then(({ Users }) => {
        this.setState({
          suggestions: Immutable.fromJS(Users.map(User => ({
            id: User.Id,
            name: User.Name
          }))),
          isLoadingMentions: false
        })
      }).catch(() => {})
    }, 200)
  }

  blur () {
    if (this.state.isFocused) {
      this.setState({
        isFocused: false
      })
    }
  }

  focusEditor () {
    this.editor && this.editor.focus()
  }

  clear () {
    this.handleChange(EditorState.push(this.state.editorState, ContentState.createFromText('')))
  }

  submit (isKeyboardEvent) {
    const { error, onSubmit } = this.props

    if (error) return

    this.setState({
      showTextFormatting: false
    })

    return new Promise((resolve, reject) => {
      let result
      try {
        result = onSubmit(this.state.content, isKeyboardEvent)

        if (isPromise(result)) {
          result.then(resolve)
        } else {
          resolve()
        }
      } catch (submitError) {
        reject(submitError)
      }
    }).then(() => {
      if (this.props.error) return

      if (!this.props.closeAfterSubmit && isKeyboardEvent && getCharCount(this.state.content) > 0) {
        this.clear()
        this.editor && this.editor.focus()
      } else {
        this.editor && this.editor.blur()
        this.clear()
      }

      this.handleUnsavedChange.flush()
    })
  }

  handleEditorRef (ref) {
    this.editor = ref
  }

  handleUnsavedChange (content, currentContent) {
    const { saveChanges, addUnsavedChange, removeUnsavedChange } = this.props

    if (saveChanges) {
      if (getCharCount(content) > 0 && !deepEqual(content, currentContent)) {
        addUnsavedChange({
          id: saveChanges,
          value: content
        })
      } else {
        removeUnsavedChange({
          id: saveChanges
        })
      }
    }
  }

  handleCharCount (editorState) {
    this.setState({
      charCount: getCharCountFromEditorState(editorState)
    })
  }

  handleChange (editorState) {
    const { onChange } = this.props

    const content = convertEditorStateToKanBoFormat(editorState)

    this.handleUnsavedChange(content, this.props.content)

    this.handleCharCount(editorState)

    this.setState({
      editorState,
      content
    })

    onChange && onChange(content)
  }

  handleClick (event) {
    if (!this.props.readOnly) {
      event.preventDefault()
      this.focusEditor()
    }
  }

  handleEditorMouseDown (event) {
    if (!this.props.readOnly) {
      event.stopPropagation()
    }
  }

  handleEditorFocus (event) {
    if (!this.state.isFocused) {
      this.setState({
        isFocused: true
      })

      this.props.onFocus && this.props.onFocus(event)
    }
  }

  handleEditorBlur (event) {
    if (this.showLinkModal) return

    this.props.onBlur && this.props.onBlur(event)

    this.blur()
  }

  handleHeaderStylesToggle (blockType) {
    this.toggleBlockType(blockType)

    setTimeout(() =>
      this.focusEditor()
    , 10)
  }

  keyBindingFn (e) {
    // Remove these key bindings
    // J
    if (e.keyCode === 74 && KeyBindingUtil.hasCommandModifier(e)) return null

    return getDefaultKeyBinding(e)
  }

  handleReturn (event) {
    const { submitting, onSubmit } = this.props

    if (onSubmit && event.ctrlKey) {
      !submitting && this.submit(true)
      return 'handled'
    }
    return 'not-handled'
  }

  handleKeyCommand (command) {
    const { editorState } = this.state

    const newState = RichUtils.handleKeyCommand(editorState, command)
    if (newState) {
      this.handleChange(newState)
      return 'handled'
    }
    return 'not-handled'
  }

  handleDrop (selection, dataTransfer, isInternal) {
    return 'handled'
  }

  onTab (e) {
    const maxDepth = 2
    this.handleChange(RichUtils.onTab(e, this.state.editorState, maxDepth))
  }

  toggleBlockType (blockType) {
    this.handleChange(
      RichUtils.toggleBlockType(
        this.state.editorState,
        blockType
      )
    )
  }

  toggleInlineStyle (inlineStyle) {
    this.handleChange(
        RichUtils.toggleInlineStyle(
          this.state.editorState,
          inlineStyle
        )
      )
  }

  handleSearchChange ({ value }) {
    this.fetchUsers(value)
  }

  handleTextFormattingClick (event) {
    event.preventDefault()

    this.setState({
      showTextFormatting: !this.state.showTextFormatting
    })
  }

  handleInsertLinkClick (event) {
    event.preventDefault()

    const { intl } = this.props
    const { editorState } = this.state

    this.showLinkModal = true

    sweetalert.queue([
      {
        text: intl.formatMessage({ id: 'LINK_URL' }),
        input: 'text',
        progressSteps: ['1', '2'],
        customClass: 'ignore-react-onclickoutside',
        cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
        showCancelButton: true
      },
      {
        text: intl.formatMessage({ id: 'LINK_TEXT' }),
        input: 'text',
        progressSteps: ['1', '2'],
        customClass: 'ignore-react-onclickoutside',
        confirmButtonText: intl.formatMessage({ id: 'LINK_INSERT' }),
        cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
        showCancelButton: true,
        animation: false
      }
    ]).then((result) => {
      const linkUrl = result[0]
      const linkText = result[1]

      const contentState = editorState.getCurrentContent()

      let contentStateWithEntity
      if (pathRegex.test(linkUrl)) {
        contentStateWithEntity = contentState.createEntity(
          'LINK',
          'IMMUTABLE',
          { url: `file:///${linkUrl}` }
        )
      } else {
        contentStateWithEntity = contentState.createEntity(
          'LINK',
          'IMMUTABLE',
          { url: addHttpToUrl(linkUrl) }
        )
      }

      const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

      const contentStateWithLink = Modifier.replaceText(
        contentStateWithEntity,
        editorState.getSelection(),
        linkText,
        null,
        entityKey
      )

      this.handleChange(EditorState.push(editorState, contentStateWithLink))
      this.focusEditor()

      this.showLinkModal = false
    }, () => {
      this.showLinkModal = false
    })
  }

  handleSubmitClick (event) {
    event.preventDefault()

    this.submit(false)
  }

  handleCancelClick (event) {
    event.stopPropagation()

    this.props.onCancel && this.props.onCancel(event)

    this.setState({
      showTextFormatting: false
    })

    this.editor && this.editor.blur()
  }

  render () {
    const {
      connectUserDropTarget,

      submitting,
      mentionBoardId,
      readOnly,
      className,
      maxLength,
      placeholder,
      expandOnFocus,
      submitButton,
      onSubmit,
      sendActions,
    } = this.props
    const {
      content,
      editorState,
      isLoadingMentions,
      suggestions,
      charCount,
      isFocused,
      showTextFormatting
    } = this.state
    const {
      MentionSuggestions
    } = this.components

    let compClassName = classes.richTextEditor
    if (!readOnly) compClassName += ' ' + classes.editable + ' form-control'
    if (isFocused) compClassName += ' focused'
    if (className) compClassName += ' ' + className

    return connectUserDropTarget(
      <div
        className={compClassName}
        onMouseDown={this.handleClick}>
        {(!readOnly && showTextFormatting) &&
          <div className={classes.controls}>
            <InlineStyleControls
              editorState={editorState}
              onToggle={this.toggleInlineStyle}
            />
            <BlockStyleControls
              blockTypes={this.blockTypes}
              editorState={editorState}
              onToggle={this.toggleBlockType}
            />
            <div className={classes.controlsGroup}>
              <Button
                className={classes.styleButton}
                icon={<KanBoIcon name='kanbo-16-link' />}
                onMouseDown={this.handleInsertLinkClick}
              />
            </div>
          </div>
        }
        <div className={classes.editorWrapper}>
          <div onMouseDown={this.handleEditorMouseDown}>
            <Editor
              ref={this.handleEditorRef}
              readOnly={readOnly}
              editorState={editorState}
              plugins={this.plugins}
              keyBindingFn={this.keyBindingFn}
              handleReturn={this.handleReturn}
              handleKeyCommand={this.handleKeyCommand}
              handleDrop={this.handleDrop}
              onFocus={this.handleEditorFocus}
              onBlur={this.handleEditorBlur}
              onChange={this.handleChange}
              onTab={this.onTab}
              placeholder={placeholder}
              blockRenderMap={extendedBlockRenderMap}
              stripPastedStyles
              spellCheck
            />
          </div>
        </div>
        {isLoadingMentions &&
          <Loader center />
        }
        {mentionBoardId &&
          <MentionSuggestions
            style={{
              display: isLoadingMentions ? 'none' : 'block'
            }}
            suggestions={suggestions}
            onSearchChange={this.handleSearchChange}
            entryComponent={MentionEntry}
          />
        }
        {(!readOnly && (isFocused || !expandOnFocus || showTextFormatting)) &&
          <div className={classes.buttons}>
            <div className={classes.leftButtons}>
              {(onSubmit && submitButton) &&
                <ButtonGroup>
                  {sendActions ? (
                    <SplitButton
                      bsStyle='primary'
                      disabled={submitting}
                      title={submitButton}
                      onMouseDown={this.handleSubmitClick}
                    >
                      {React.Children.map(sendActions, sendAction => {
                        if (!React.isValidElement(sendAction)) {
                          return null
                        }

                        return React.cloneElement(
                          sendAction,
                          {
                            onClick: event => {
                              this.handleCancelClick(event)
  
                              sendAction.props.onClick && sendAction.props.onClick(event, content, this.clear)
                            }
                          },
                        )
                      })}
                    </SplitButton>
                  ) : (
                    <Button
                      bsStyle='primary'
                      disabled={submitting}
                      onMouseDown={this.handleSubmitClick}
                    >
                      {submitButton}
                    </Button>
                  )}
                  <Button
                    onMouseDown={this.handleCancelClick}
                  >
                    <FormattedMessage id='CANCEL' />
                  </Button>
                </ButtonGroup>
              }
            </div>
            <div className={classes.rightButtons}>
              {maxLength &&
                <span className={classes.remainingCharacters}>
                  {charCount}{'/'}{maxLength}
                </span>
              }
              <Button
                className={classes.styleButton + ' m-l-5'}
                bsStyle={showTextFormatting ? 'primary' : 'default'}
                icon={<KanBoIcon name='kanbo-16-letter-count' />}
                onMouseDown={this.handleTextFormattingClick}
              />
            </div>
          </div>
        }
      </div>
    )
  }
}

const userDropTarget = {
  canDrop (props) {
    return !!props.mentionBoardId
  },

  drop (props, monitor, component) {
    const { id: userId, name: userName } = monitor.getItem()

    const instance = component.getInstance()

    const editorState = instance.state.editorState

    const selection = editorState.getSelection()
    const end = selection.getEndOffset()

    const contentState = editorState.getCurrentContent()
    const contentStateWithEntity = contentState.createEntity(
      'mention',
      'IMMUTABLE',
      {
        mention: fromJS({
          id: userId,
          name: userName
        })
      }
    )
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

    let contentStateWithMention = Modifier.replaceText(
      contentStateWithEntity,
      selection,
      userName,
      null,
      entityKey
    )

    // If the mention is inserted at the end, a space is appended right after for
    // a smooth writing experience.
    const blockKey = selection.getAnchorKey()
    const blockSize = contentState.getBlockForKey(blockKey).getLength()

    if (blockSize === end) {
      contentStateWithMention = Modifier.insertText(
        contentStateWithMention,
        contentStateWithMention.getSelectionAfter(),
        ' '
      )
    }

    instance.handleChange(EditorState.push(editorState, contentStateWithMention))
    instance.focusEditor()
  }
}


export default
injectIntl(
  DropTarget(userDragType, userDropTarget, (connect, monitor) => ({
    connectUserDropTarget: connect.dropTarget(),
    isUserOver: monitor.isOver()
  }))(
    onClickOutside(RichTextEditor)
  )
)

